// auth.js — auth handshake against the Home Assistant instance.
//
// Two paths depending on environment:
//
//   1. **Long-lived access token** (dev convenience). When the build
//      sees a non-empty `VITE_HASS_LL_TOKEN`, we skip the OAuth dance
//      entirely and create an Auth object from the static token.
//      Generate the token at HA → Profile → Security →
//      "Long-Lived Access Tokens" → "Create Token", paste into
//      panel/.env as VITE_HASS_LL_TOKEN=ey...
//
//   2. **OAuth** (production). Uses home-assistant-js-websocket's
//      getAuth() helper. PWA redirects to HA's /auth/authorize,
//      HA shows the login UI (or auto-grants via trusted_networks
//      on LAN), HA redirects back with ?code=...&state=..., the
//      library exchanges code for tokens, persists them to
//      localStorage, and getAuth() resolves with the Auth object.
//
// Env / build flags:
//   VITE_HASS_URL       — HA instance URL. Required in prod. In dev
//                         leave empty; the Vite proxy makes /api +
//                         /auth same-origin to localhost.
//   VITE_HASS_LL_TOKEN  — long-lived access token. Dev shortcut.

import {
  getAuth,
  createLongLivedTokenAuth,
} from 'home-assistant-js-websocket';

const STORAGE_KEY = 'ha-controller-pwa.auth';

const HASS_URL =
  import.meta.env.VITE_HASS_URL ??
  (import.meta.env.DEV ? '' : window.location.origin);

// SECURITY: long-lived tokens grant full HA control. If we baked one
// into a public production bundle, anyone with the URL could read it.
// Therefore: only consult VITE_HASS_LL_TOKEN in dev mode. Production
// builds ALWAYS go through real OAuth, even if the env var is set.
const LL_TOKEN = import.meta.env.DEV
  ? (import.meta.env.VITE_HASS_LL_TOKEN || '')
  : '';

const loadTokens = async () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveTokens = (data) => {
  try {
    if (data) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* localStorage unavailable */
  }
};

// Acquire an Auth object. Dev with LL token: instant, no redirect.
// Otherwise: full OAuth flow (may redirect the browser).
export async function acquireAuth() {
  if (LL_TOKEN) {
    // hassUrl must be a non-empty URL for the WS connection logic;
    // when running through the Vite proxy with empty VITE_HASS_URL
    // we synthesise the current origin so the library can compose
    // ws://localhost:<port>/api/websocket (which the proxy upgrades
    // to wss://<HA>).
    const hassUrl = HASS_URL || window.location.origin;
    return createLongLivedTokenAuth(hassUrl, LL_TOKEN);
  }
  return getAuth({
    hassUrl: HASS_URL,
    saveTokens,
    loadTokens,
  });
}

// After OAuth: strip ?auth_callback=1&code=... from window.location.
export function cleanOAuthUrl() {
  const url = new URL(window.location.href);
  if (
    url.searchParams.has('auth_callback') ||
    url.searchParams.has('code') ||
    url.searchParams.has('state')
  ) {
    url.searchParams.delete('auth_callback');
    url.searchParams.delete('code');
    url.searchParams.delete('state');
    window.history.replaceState({}, '', url.toString());
  }
}

// Drop persisted tokens without revoking (used when HA says they're
// already invalid — revoke would just fail).
export function clearStoredTokens() {
  saveTokens(null);
}

export async function signOut(auth) {
  try {
    if (auth && !LL_TOKEN) await auth.revoke();
  } catch {
    /* best effort */
  }
  saveTokens(null);
  window.location.reload();
}
